import { useState, useContext, useMemo, useEffect } from "react";
import { Context } from "../../Context/Context";
import { postsApi, uploadApi } from "../../services";
import RankEditor from "../../components/rank/RankEditor";
import "./write.css";

// Số ảnh upload đồng thời mỗi lô (tránh mở 20-30 kết nối cùng lúc gây nghẽn)
const UPLOAD_BATCH_SIZE = 4;

export default function Write() {
  const [title, setTitle] = useState();
  const [desc, setDesc] = useState();
  const [price, setPrice] = useState();
  const [priceValue, setPriceValue] = useState("");
  const [file, setFile] = useState(null);
  const [listImages, setListImages] = useState([]);
  const [type, setType] = useState();
  // Rank & uy tín — chỉ dùng cho acc Liên Quân, xem RankEditor
  const [rankInfo, setRankInfo] = useState({ rank: null, reputation: null });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null); // { done, total }
  const { user } = useContext(Context);

  // Create blob preview URLs once per file (not on every render/keystroke),
  // and revoke them on cleanup to avoid the browser re-fetching + memory leaks.
  const filePreview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => { if (filePreview) URL.revokeObjectURL(filePreview); };
  }, [filePreview]);

  const imagePreviews = useMemo(
    () => listImages.map((f) => URL.createObjectURL(f)),
    [listImages]
  );
  useEffect(() => {
    return () => { imagePreviews.forEach((url) => URL.revokeObjectURL(url)); };
  }, [imagePreviews]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setListImages([...listImages, ...selectedFiles]);
  };

  const handleRemoveFile = (index) => {
    const newFiles = [...listImages];
    newFiles.splice(index, 1);
    setListImages(newFiles);
  };

  const onChangeValueType = (e) => {
    setType(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");

    const formatPrice = (priceStr) => {
      if (!priceStr) return "1h/0";
      if (priceStr.includes('/')) return priceStr;
      const cleanPrice = priceStr.replace(/[^\d]/g, '');
      if (cleanPrice) return `3h/${cleanPrice}`;
      return "3h/0";
    };

    const newPost = {
      username: user.username,
      title: title,
      desc: desc || "",
      price: formatPrice(price),
      priceValue: Number(priceValue) || 0,
      thumb: type || "crossFire",
      listImages: [],
    };

    // Rank/uy tín chỉ có nghĩa với acc Liên Quân — loại khác gửi lên sẽ bị bỏ qua ở đây
    if (newPost.thumb === "lienquan") {
      newPost.rank = rankInfo.rank;
      newPost.reputation = rankInfo.reputation;
    }

    if (!newPost.title || !newPost.price) {
      setError("Vui lòng điền đầy đủ mã tài khoản và giá thuê!");
      return;
    }

    setSubmitting(true);

    try {
      // ── Bước 1: Kiểm tra trùng mã tài khoản TRƯỚC khi upload ảnh ──
      const { exists } = await postsApi.checkTitleExists(newPost.title);
      if (exists) {
        setError(`Mã tài khoản "${newPost.title}" đã tồn tại! Vui lòng chọn mã khác.`);
        setSubmitting(false);
        return;
      }

      // ── Bước 2: Upload ảnh ──
      // Gộp ảnh chính + ảnh chi tiết để báo tiến độ chung
      const allFiles = [...(file ? [file] : []), ...listImages];
      // Dùng tên file THẬT do server trả về (server slug hóa + đổi đuôi jpg/webp),
      // không tự suy ra tên — nếu không URL sẽ lệch và ảnh bị 404.
      // Upload theo lô để không mở quá nhiều kết nối cùng lúc (20-30 ảnh sẽ nghẽn).
      const total = allFiles.length;
      if (total > 0) setProgress({ done: 0, total });

      const uploadedNames = [];
      const counter = { done: 0 };
      for (let i = 0; i < allFiles.length; i += UPLOAD_BATCH_SIZE) {
        const batch = allFiles.slice(i, i + UPLOAD_BATCH_SIZE);
        const batchResults = await Promise.all(
          batch.map(async (imgFile, j) => {
            // tên tạm duy nhất (tránh đè file tạm khi trùng tên hoặc cùng mốc ms)
            const tempName = `${Date.now()}-${i + j}-${imgFile.name}`;
            const res = await uploadApi.uploadFile(imgFile, tempName);
            counter.done += 1;
            setProgress({ done: counter.done, total });
            return res?.filename || tempName;
          })
        );
        uploadedNames.push(...batchResults);
      }

      // Phần tử đầu là ảnh chính (nếu có), phần còn lại là ảnh chi tiết
      if (file) {
        newPost.photo = uploadedNames[0];
        newPost.listImages.push(...uploadedNames.slice(1));
      } else {
        newPost.listImages.push(...uploadedNames);
      }

      setProgress(null);
      const res = await postsApi.createPost(newPost);
      // Thành công -> chuyển trang; giữ nút disabled trong lúc điều hướng.
      window.location.replace("/post/" + res._id);
    } catch (err) {
      console.error("Create post error:", err);
      const apiData = err?.response?.data;
      // MongoDB duplicate key error (code: 11000) — fallback nếu pre-check bị race condition
      if (apiData?.code === 11000 || apiData?.keyPattern?.title) {
        setError(`Mã tài khoản "${newPost.title}" đã tồn tại! Vui lòng chọn mã khác.`);
      } else {
        const apiMsg = apiData?.message || apiData;
        setError(
          (typeof apiMsg === "string" && apiMsg) ||
            err?.message ||
            "Tạo tài khoản thất bại. Vui lòng thử lại."
        );
      }
      setProgress(null);
      setSubmitting(false);
    }
  };

  return (
    <div className="write-page">
      <div className="write-container">
        <div className="write-header">
          <h1>Tạo tài khoản mới</h1>
          <p>Điền thông tin chi tiết để tạo tài khoản game</p>
        </div>

        <form className="write-form" onSubmit={handleSubmit}>
          {/* Main Image Upload */}
          <div className="form-section">
            <label className="form-label">Ảnh chính</label>
            <div className="image-upload-area">
              {file ? (
                <div className="image-preview-main">
                  <img src={filePreview} alt="Xem trước" />
                  <button
                    type="button"
                    className="remove-main-image"
                    onClick={() => setFile(null)}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ) : (
                <label htmlFor="fileInput" className="upload-button">
                  <i className="fas fa-plus"></i>
                  <span>Tải ảnh lên</span>
                </label>
              )}
              <input
                id="fileInput"
                type="file"
                style={{ display: "none" }}
                onChange={(e) => setFile(e.target.files[0])}
                accept="image/*"
              />
            </div>
          </div>

          {/* Account Type */}
          <div className="form-section">
            <label className="form-label">Loại tài khoản</label>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="type_account"
                  value="crossFire"
                  onChange={onChangeValueType}
                />
                <span className="radio-custom"></span>
                <span className="radio-label">CrossFire</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="type_account"
                  value="lienquan"
                  onChange={onChangeValueType}
                />
                <span className="radio-custom"></span>
                <span className="radio-label">Liên Quân</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="type_account"
                  value="freefire"
                  onChange={onChangeValueType}
                />
                <span className="radio-custom"></span>
                <span className="radio-label">Free Fire</span>
              </label>
            </div>
          </div>

          {/* Rank & uy tín — chỉ hiện với acc Liên Quân */}
          {type === "lienquan" && (
            <div className="form-section">
              <label className="form-label">Rank &amp; uy tín</label>
              <RankEditor value={rankInfo} onChange={setRankInfo} />
            </div>
          )}

          {/* Account ID */}
          <div className="form-section">
            <label className="form-label">Mã tài khoản <span className="required">*</span></label>
            <div className="input-group">
              <i className="fas fa-hashtag input-icon"></i>
              <input
                className="form-input"
                placeholder="2323 - Tên tài khoản"
                type="text"
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
          </div>

          {/* Account Info */}
          <div className="form-section">
            <label className="form-label">Thông tin tài khoản</label>
            <div className="input-group">
              <i className="fas fa-info-circle input-icon"></i>
              <textarea
                className="form-textarea"
                placeholder="78 vip - vip 7"
                onChange={(e) => setDesc(e.target.value)}
                rows="3"
              />
            </div>
          </div>

          {/* Account Price */}
          <div className="form-section">
            <label className="form-label">Giá thuê <span className="required">*</span></label>
            <div className="input-group">
              <i className="fas fa-tag input-icon"></i>
              <input
                className="form-input"
                placeholder="3h/60.000 hoặc 60.000"
                type="text"
                onChange={(e) => setPrice(e.target.value)}
              />
            </div>
          </div>

          {/* Price Value */}
          <div className="form-section">
            <label className="form-label">Giá tính tiền (₫)</label>
            <div className="input-group">
              <i className="fas fa-coins input-icon"></i>
              <input
                className="form-input"
                placeholder="60000"
                type="number"
                min="0"
                step="1000"
                onChange={(e) => setPriceValue(e.target.value)}
              />
            </div>
          </div>

          {/* Additional Images */}
          <div className="form-section">
            <label className="form-label">Ảnh chi tiết</label>
            <div className="additional-images">
              <label htmlFor="fileInputImgs" className="add-image-btn">
                <i className="fas fa-plus"></i>
                <span>Thêm ảnh</span>
              </label>
              <input
                id="fileInputImgs"
                type="file"
                style={{ display: "none" }}
                onChange={handleFileChange}
                multiple
                accept="image/*"
              />

              <div className="image-grid">
                {listImages.map((file, index) => (
                  <div key={index} className="image-item">
                    <img src={imagePreviews[index]} alt="" />
                    <button
                      type="button"
                      className="remove-image"
                      onClick={() => handleRemoveFile(index)}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            {error && (
              <div className="form-error" role="alert">
                <i className="fas fa-exclamation-circle"></i>
                <span>{error}</span>
              </div>
            )}
            <button className="submit-btn" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>
                    {progress
                      ? `Đang tải ảnh ${progress.done}/${progress.total}...`
                      : "Đang tạo..."}
                  </span>
                </>
              ) : (
                <>
                  <i className="fas fa-plus"></i>
                  <span>Tạo tài khoản</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
